import { defaultDataIdFromObject } from "@apollo/client";
import update from "immutability-helper";

export const dataIdFromObject = object => {
  switch (object.__typename) {
    case "BenchmarkLevel": {
      return null;
    }
    case "StudentTranscript": {
      return null;
    }
    case "ConfigurablePermission": {
      return null;
    }
    case "TPSEntity": {
      return null;
    }
    /**  Since backend is returning same id for multiple objects of
     * PlannerElementSetConfig, we are disabling its normalization */
    case "PlannerElementSetConfig": {
      return null;
    }
    case "ATLLevel":
      return null;
    case "ConfigurationValuesResponse": {
      const { id, regionId } = object;
      return `ConfigurationValuesResponse:${id}:${regionId}`;
    }
    default:
      return defaultDataIdFromObject(object); // fall back to default handling
  }
};

// adding a replica of the function to avoid circular dependency

export const setJsonItem = ({ key, value }) => {
  try {
    const serializedValue = JSON.stringify(value);
    localStorage.setItem(key, serializedValue);
  } catch (err) {
    console.error(err);
  }
};

/**
 * ---------------------------------------------------------------
 * In this section, we are generating dynamic id for each object not having id and having __typename.
 * To generate dynamic id, we are using response returned from graphql server.
 * However, only response is not sufficient to generate dynamic id. We also need to consider variables on fields and fragments.
 * To get this information, we are using operation object which is passed by apollo client in link.
 *
 * From operation object, we are creating field tree which represents relationships between different fields.
 * Each node of the tree consists of field name, variables map and child nodes(subfields)
 *
 * To get more idea on operations object and corresponding field tree, please refer to below examples:
 * https://codesandbox.io/s/apollo-cache-new-playground-sxdh0v?file=/src/structureOfOperationObject1.js
 * https://codesandbox.io/s/apollo-cache-new-playground-sxdh0v?file=/src/structureOfOperationObject2.js
 *
 * ---------------------------------------------------------------
 */

/**
 * ---- Functions to create field tree from operation object ----
 */

/**
 * This function sorts variables of a field in alphabetical order to create consistent id for that field.
 */
const _sortVariables = (variables = {}) => {
  const keys = Object.keys(variables).sort();
  const data = {};
  for (let i = 0; i < keys.length; i++) {
    data[keys[i]] = variables[keys[i]];
  }
  return data;
};

/**
 * This functions create variables of a field using schema declaration and variables passed in query. It takes two parameters:
 * 1. fieldArguments - arguments of a fields declared in query or mutation.
 * 2. queryVariables - variables passed to query or mutation.
 */
const _prepareFieldVariablesFromArguments = ({
  fieldArguments,
  queryVariables,
}) => {
  const fieldVariables = {};

  const args = fieldArguments ?? [];
  for (let i = 0; i < args.length; i++) {
    const argument = args[i];
    /**
     * Each argument contains at least two properties:
     * 1. name - name of the argument defined in query or mutation.
     * 2. value - actual name of the argument which is passed to query or mutation.
     * Ex. If we have schema like below, then name of the argument is "id" and value of the argument is "organizationId".
     *  organization(id: $organizationId)
     */

    const argumentName = argument?.name?.value ?? "";

    const argumentValueKind = argument?.value?.kind ?? "";

    switch (argumentValueKind) {
      /**
       * This case is used when we pass all variables as single object.
       */
      case "Variable": {
        const variableName = argument?.value?.name?.value ?? "";
        const variableValue = queryVariables[variableName];

        let sortedVariableValue = variableValue;

        /**
         * If variable value is an object, then we need to sort it to create consistent id.
         */
        if (
          typeof variableValue === "object" &&
          variableValue !== null &&
          !Array.isArray(variableValue)
        ) {
          sortedVariableValue = _sortVariables(variableValue);
        }

        fieldVariables[argumentName] = sortedVariableValue;

        break;
      }

      /**
       * This case is used when we pass individual properties as variables instead of a single object.
       */
      case "ObjectValue": {
        const objectValueFields = argument?.value?.fields ?? [];

        const objectValue = _prepareFieldVariablesFromArguments({
          fieldArguments: objectValueFields,
          queryVariables,
        });

        fieldVariables[argumentName] = objectValue;

        break;
      }
      /**
       * This case is used when variables are hardcoded into query or mutation or for default values.
       */

      default: {
        const argumentValue = argument?.value?.value;

        fieldVariables[argumentName] = argumentValue;
      }
    }
  }

  return _sortVariables(fieldVariables);
};

/**
 * This function creates field tree from operation object. Main aim of the field tree is to store variables of each field.
 * It takes four parameters:
 * 1. definitions - definitions of the query or mutation. It represents information of all fragments and query(or mutation).
 * 2. selectionSet - selection set of the query or mutation. It represents all fields which are requested in query or mutation.
 * 3. parentNode - parent node of the field tree.
 * 4. queryVariables - variables passed to query or mutation.
 */
const _populateFieldTree = ({
  definitions,
  selectionSet,
  parentNode,
  queryVariables,
}) => {
  const { selections } = selectionSet;

  for (let i = 0; i < selections.length; i++) {
    const selection = selections[i];
    const {
      kind,
      alias,
      arguments: fieldArguments,
      name,
      selectionSet,
      typeCondition,
    } = selection;

    const node = {
      kind,
    };

    switch (kind) {
      /**
       * This case is applicable for normal fields like id,name, organization etc.
       */
      case "Field": {
        const fieldName = name?.value;
        const aliasName = alias?.value;

        //If we have done aliasing consider that name instead of actual name.
        const finalFieldName = aliasName ? aliasName : fieldName;
        node.field = finalFieldName;
        node.schemaFieldName = fieldName;
        node.aliasFieldName = aliasName;

        parentNode[finalFieldName] = node;

        const fieldVariables = _prepareFieldVariablesFromArguments({
          fieldArguments,
          queryVariables,
        });

        if (Object.keys(fieldVariables).length > 0) {
          node.variablesMap = fieldVariables;
        }

        if (selectionSet?.selections?.length > 0) {
          _populateFieldTree({
            definitions,
            selectionSet,
            parentNode: node,
            queryVariables,
          });
        }

        break;
      }

      /**
       * This case is applicable when we have interface or union type.
       */
      case "InlineFragment": {
        const typeName = typeCondition?.name?.value;
        node.field = typeName;
        parentNode[typeName] = node;
        if (selectionSet?.selections?.length > 0) {
          _populateFieldTree({
            definitions,
            selectionSet,
            parentNode: node,
            queryVariables,
          });
        }

        break;
      }

      /**
       * This case is applicable when we have used a fragment in query or mutation.
       */
      case "FragmentSpread": {
        const fragmentName = name?.value;
        const newDefinition = definitions.find(
          ({ name }) => name?.value == fragmentName
        );

        const { selectionSet } = newDefinition;

        _populateFieldTree({
          definitions,
          selectionSet,
          parentNode,
          queryVariables,
        });
        break;
      }
    }
  }
};

const _createFieldTree = operation => {
  const { query, variables, dataId } = operation;
  const { definitions } = query;

  const queryDefinition = definitions[0];

  const { selectionSet } = queryDefinition;

  const treeRootNode = {};

  try {
    _populateFieldTree({
      definitions,
      selectionSet,
      parentNode: treeRootNode,
      queryVariables: variables,
    });
  } catch (e) {
    console.error(
      "Error while creating field tree for ",
      operation.operationName || dataId,
      e
    );
  }

  return treeRootNode;
};

/**
 * ---- Functions to populate dynamic id ----
 */

const _isPrimitiveValue = data => !(data?.__typename || Array.isArray(data));

const _ROOT_ENTITY_TYPES = new Set([
  "SchoolQuery",
  "DocumentationQuery",
  "PlanningQuery",
  "PlannerQuery",
  "PlatformQuery",
  "GlobalConstants",
  "OrganizationManagementQuery",
  "CommunityQuery",
  "CmsQuery",
  "TaskManagmentQuery",
  "IntegrationQuery",
  "ContentQuery",
  "AiQuery",
  "DocumentationMutations",
  "SchoolMutations",
  "PlatformMutations",
  "PlannerMutations",
  "OrganizationManagementMutations",
  "TaskManagementMutations",
  "CommunityMutations",
  "IntegrationMutation",
  "CmsMutations",
  "ContentMutations",
  "AiMutations",
]);

/**
 * FNV-1a inspired hash producing a 16-char hex string (64 bits).
 * Two independent 32-bit hashes are concatenated for collision resistance.
 * At 25K cache entries the collision probability is ~0.00003%.
 */
const _hashString = str => {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    h1 ^= c;
    h1 = Math.imul(h1, 0x01000193);
    h2 ^= c;
    h2 = Math.imul(h2, 0x811c9dc5);
  }
  return (
    (h1 >>> 0).toString(16).padStart(8, "0") +
    (h2 >>> 0).toString(16).padStart(8, "0")
  );
};

const _variablesHashCache = new WeakMap();
const _hashVariables = obj => {
  if (!obj) return "";
  let cached = _variablesHashCache.get(obj);
  if (cached === undefined) {
    cached = _hashString(JSON.stringify(obj));
    _variablesHashCache.set(obj, cached);
  }
  return cached;
};

const _populateDynamicIdForObject = ({ data, parenId, fieldTree }) => {
  //For special non normalized types, we need to ignore default ids. Hence, hasIdProperty will always be false for them.
  const hasIdProperty = !!data.id;

  const fieldTypeName = data.__typename;
  const entityId = data.id;
  const keys = Object.keys(data);
  const result = {};

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];

    //Here, first condition is for fields that belong inside an interface or union type.
    const fieldNode =
      (fieldTypeName && fieldTree[fieldTypeName]?.[key]) ||
      fieldTree[key] ||
      {};

    const variablesMap = fieldNode.variablesMap;

    /**
     * Due to aliasing, we might have different name for field in schema and query.
     * So, to generate id, we will use schema name.
     */
    const fieldName = fieldNode.schemaFieldName;

    let newParentId = hasIdProperty
      ? `${fieldTypeName}:${entityId}.${fieldName}`
      : `${parenId}.${fieldName}`;

    if (variablesMap) {
      newParentId = `${newParentId}(${_hashVariables(variablesMap)})`;
    }

    result[key] = _populateDynamicId({
      data: data[key],
      parenId: newParentId,
      fieldTree: fieldNode,
    });
  }

  // Set _id AFTER the loop so it always overwrites any server-side _id,
  // matching the original behavior where updatedData._id = parenId was set
  // before iteration on a shallow copy.
  if (!hasIdProperty) {
    result._id = parenId;
  }

  return result;
};

/**
 * This function injects dynamic id into each non-normalized object.
 * It relies on data returned from server and fieldTree corresponding to the data
 */
const _populateDynamicId = ({ data, parenId, fieldTree }) => {
  if (_isPrimitiveValue(data)) {
    return data;
  }

  if (data && !fieldTree) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "Fields for data are not present in fragment/query definition. Please verify fragment/query definition and data.",
        data
      );
    }
    return data;
  }

  if (Array.isArray(data)) {
    return data.map((value, index) => {
      return _populateDynamicId({
        data: value,
        parenId: `${parenId}.${index}`,
        fieldTree,
      });
    });
  }

  if (typeof data === "object" && data) {
    return _populateDynamicIdForObject({ data, parenId, fieldTree });
  }
};

/**
 * ---- Main functions to populate dynamic id ----
 */

export const populateDynamicId = ({ data, operation }) => {
  const { dataId } = operation;

  if (dataId === "ROOT_QUERY" || dataId === "ROOT_MUTATION") {
    return _populateDynamicIdForQuery({
      data,
      operation,
    });
  }

  return _populateDynamicIdForFragment({ data, operation });
};

const _populateDynamicIdForQuery = ({ data, operation }) => {
  const keys = Object.keys(data ?? {});

  /**
   * parentPropertyOfQueryData can be platform, planner, etc.(Top level query fields)
   */
  const parentPropertyOfQueryData = keys.find(key => key !== "__typename");
  const queryData = data[parentPropertyOfQueryData];

  const { __typename } = queryData;

  const initialId = _ROOT_ENTITY_TYPES.has(__typename)
    ? __typename
    : `ROOT_QUERY_${parentPropertyOfQueryData}`;

  const fieldTree = _createFieldTree(operation);

  try {
    const updatedQueryData = _populateDynamicId({
      data: queryData,
      parenId: initialId,
      fieldTree: fieldTree[parentPropertyOfQueryData],
    });

    return update(data, {
      [parentPropertyOfQueryData]: {
        $set: updatedQueryData,
      },
    });
  } catch (e) {
    console.error(
      "Error while populating dynamic id for ",
      operation.operationName,
      e
    );
    return data;
  }
};

const _populateDynamicIdForFragment = ({ data, operation }) => {
  const fieldTree = _createFieldTree(operation);

  try {
    return _populateDynamicId({
      data,
      parenId: operation.dataId,
      fieldTree,
    });
  } catch (e) {
    console.error(
      "Error while populating dynamic id for Object",
      operation.dataId,
      " ",
      e
    );
    return data;
  }
};
